import { Link, router, useForm, usePage } from '@inertiajs/react';
import { useId, useState } from 'react';
import type { FormEvent } from 'react';

import { ErrorSummary } from '@/components/public/forms/ErrorSummary';
import FormActions from '@/components/public/forms/FormActions';
import { SubmissionReceipt } from '@/components/public/forms/SubmissionReceipt';
import DatePicker from '@/components/public/ui/DatePicker';
import Select from '@/components/public/ui/Select';
import TimePicker from '@/components/public/ui/TimePicker';
import { trackUmamiEvent } from '@/lib/analytics';
import { buildErrorSummaryItems } from '@/lib/errorSummary';
import { inputClass, inputErrorClass, sectionLabel } from '@/lib/formStyles';
import {
  futureDateError,
  timeRangeError,
  todayISODate,
  validateEmail,
  validatePhonePT,
} from '@/lib/validators';

type FlashProps = { flash: { notice?: string; alert?: string } };

type Payload = {
  name: string;
  entity: string;
  email: string;
  phone: string;
  space: string;
  eventPurpose: string;
  date: string;
  startTime: string;
  endTime: string;
  attendees: string;
  notes: string;
};

type Option = { value: string; label: string };

const initialPayload: Payload = {
  name: '',
  entity: '',
  email: '',
  phone: '',
  space: '',
  eventPurpose: '',
  date: '',
  startTime: '',
  endTime: '',
  attendees: '',
  notes: '',
};

const FIELD_LABELS: Record<string, string> = {
  name: 'Nome completo',
  entity: 'Entidade / Associação',
  email: 'Correio eletrónico',
  phone: 'Telefone',
  space: 'Espaço pretendido',
  eventPurpose: 'Finalidade do evento',
  date: 'Data',
  startTime: 'Hora de início',
  endTime: 'Hora de fim',
  attendees: 'N.º estimado de participantes',
  notes: 'Observações',
  consent: 'Consentimento',
  files: 'Anexos',
  type: 'Tipo de pedido',
};

const FIELD_ANCHORS = new Set([
  'name',
  'entity',
  'email',
  'phone',
  'space',
  'eventPurpose',
  'date',
  'startTime',
  'endTime',
  'attendees',
  'notes',
]);

export default function VenueBookingForm() {
  const { flash, venues } = usePage<FlashProps & { venues: Option[] }>().props;
  const idPrefix = useId();
  const fid = (name: string) => `${idPrefix}-${name}`;
  const errId = (name: string) => `${idPrefix}-${name}-error`;

  const form = useForm<{
    payload: Payload;
    files: File[];
    consentTerms: boolean;
    consentData: boolean;
    consented_at: string;
  }>({
    payload: initialPayload,
    files: [],
    consentTerms: false,
    consentData: false,
    consented_at: '',
  });

  const { data, setData, processing, errors: rawErrors, reset } = form;
  const errors = rawErrors as unknown as Record<string, string | undefined>;

  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});

  const set =
    (field: keyof Payload) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setData('payload', { ...data.payload, [field]: e.target.value });

  const validateField = (field: string, value: string): string | null => {
    if (!value) return null;
    if (field === 'phone' && !validatePhonePT(value)) return 'Número de telefone inválido.';
    if (field === 'email' && !validateEmail(value)) return 'Correio eletrónico inválido.';
    if (field === 'date') return futureDateError(value);
    return null;
  };

  const handleBlur = (field: keyof Payload) => () => {
    const value = String(data.payload[field]);
    const err = validateField(field, value);
    setClientErrors((prev) => {
      const next = { ...prev };
      if (err) next[field] = err;
      else delete next[field];
      return next;
    });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const phoneErr = validateField('phone', data.payload.phone);
    if (phoneErr) errs.phone = phoneErr;
    const emailErr = validateField('email', data.payload.email);
    if (emailErr) errs.email = emailErr;
    const dateErr = validateField('date', data.payload.date);
    if (dateErr) errs.date = dateErr;
    const timeErr = timeRangeError(data.payload.startTime, data.payload.endTime);
    if (timeErr) errs.endTime = timeErr;
    if (Object.keys(errs).length > 0) {
      setClientErrors(errs);
      trackUmamiEvent('form_submit_blocked', {
        form_type: 'venue_booking',
        error_count: Object.keys(errs).length,
      });
      return;
    }
    form.transform((d) => ({
      type: 'venue_booking',
      consent: 'true',
      consented_at: new Date().toISOString(),
      payload: d.payload,
      files: d.files,
    }));
    trackUmamiEvent('service_request_submit', {
      service_type: 'venue_booking',
      has_files: data.files.length > 0,
    });
    form.post('/service-requests', { forceFormData: true });
  };

  const fieldErr = (field: string) => clientErrors[field] || errors[field];
  const errorItems = buildErrorSummaryItems({ ...errors, ...clientErrors }, FIELD_LABELS, (key) =>
    FIELD_ANCHORS.has(key) ? `#${fid(key)}` : undefined,
  );

  const p = data.payload;
  const isFormValid =
    !!p.name &&
    !!p.email &&
    !!p.phone &&
    !!p.space &&
    !!p.eventPurpose &&
    !!p.date &&
    !!p.startTime &&
    !!p.endTime &&
    !!p.attendees &&
    data.consentTerms &&
    data.consentData &&
    Object.keys(clientErrors).length === 0;

  if (flash?.notice) {
    return (
      <SubmissionReceipt
        formType="venue_booking"
        title="Reserva submetida à Junta"
        message={flash.notice}
        steps={[
          { label: 'Registo', description: 'A reserva ficou guardada com data e consentimento.' },
          {
            label: 'Análise',
            description: 'Os serviços confirmam disponibilidade e taxas aplicáveis.',
          },
          {
            label: 'Contacto',
            description: 'Os serviços entrarão em contacto para confirmação ou dados adicionais.',
          },
        ]}
        actions={[
          {
            label: 'Submeter outro pedido',
            onClick: () => {
              reset();
              setClientErrors({});
              router.get(window.location.pathname, {}, { replace: true, preserveScroll: true });
            },
          },
          { label: 'Voltar aos serviços', href: '/services', variant: 'primary' },
        ]}
      />
    );
  }

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="space-y-8">
        <ErrorSummary items={errorItems} />

        {/* Info alert */}
        <div className="border-primary/20 bg-primary/[0.04] text-on-surface-light rounded-xl border px-4 py-3 text-sm">
          <p className="text-on-surface font-medium">Utilização sujeita a taxas</p>
          <p className="mt-1">
            A cedência dos espaços pode estar sujeita ao pagamento de taxas, conforme o regulamento
            da Junta de Freguesia em vigor. Será informado(a) do valor aplicável após análise do
            pedido.
          </p>
        </div>

        {/* Requerente */}
        <div>
          <span className={sectionLabel('secondary')}>Requerente</span>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('name')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Nome completo
              </label>
              <input
                id={fid('name')}
                className={inputClass}
                value={p.name}
                onChange={set('name')}
                placeholder="Ex: João Ferreira"
                required
              />
            </div>
            <div>
              <label
                htmlFor={fid('entity')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Entidade / Associação{' '}
                <span className="text-on-surface-light font-normal">(opcional)</span>
              </label>
              <input
                id={fid('entity')}
                className={inputClass}
                value={p.entity}
                onChange={set('entity')}
                placeholder="Ex: Rancho Folclórico local"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('email')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Correio eletrónico
              </label>
              <input
                id={fid('email')}
                type="email"
                className={fieldErr('email') ? inputErrorClass : inputClass}
                value={p.email}
                onChange={set('email')}
                onBlur={handleBlur('email')}
                aria-invalid={!!fieldErr('email')}
                aria-describedby={fieldErr('email') ? errId('email') : undefined}
                placeholder="email@exemplo.pt"
                required
              />
              {fieldErr('email') && (
                <p id={errId('email')} className="text-error mt-1 text-xs">
                  {fieldErr('email')}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor={fid('phone')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Telefone
              </label>
              <input
                id={fid('phone')}
                type="tel"
                className={fieldErr('phone') ? inputErrorClass : inputClass}
                value={p.phone}
                onChange={set('phone')}
                onBlur={handleBlur('phone')}
                aria-invalid={!!fieldErr('phone')}
                aria-describedby={fieldErr('phone') ? errId('phone') : undefined}
                placeholder="+351 9XX XXX XXX"
                required
              />
              {fieldErr('phone') && (
                <p id={errId('phone')} className="text-error mt-1 text-xs">
                  {fieldErr('phone')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Detalhes da Reserva */}
        <div>
          <span className={sectionLabel('secondary')}>Detalhes da Reserva</span>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('space')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Espaço Pretendido
              </label>
              <Select id={fid('space')} value={p.space} onChange={set('space')} required>
                <option value="">Selecionar espaço</option>
                {venues.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label
                htmlFor={fid('eventPurpose')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Finalidade do Evento
              </label>
              <input
                id={fid('eventPurpose')}
                className={inputClass}
                value={p.eventPurpose}
                onChange={set('eventPurpose')}
                placeholder="Ex: Reunião de associação"
                required
              />
            </div>
          </div>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label
                htmlFor={fid('date')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Data
              </label>
              <DatePicker
                id={fid('date')}
                value={p.date}
                onChange={(value) => {
                  setData('payload', { ...p, date: value });
                  const err = validateField('date', value);
                  setClientErrors((prev) => {
                    const next = { ...prev };
                    if (err) next.date = err;
                    else delete next.date;
                    return next;
                  });
                }}
                min={todayISODate()}
                hasError={!!fieldErr('date')}
                aria-invalid={!!fieldErr('date')}
                aria-describedby={fieldErr('date') ? errId('date') : undefined}
                required
              />
              {fieldErr('date') && (
                <p id={errId('date')} className="text-error mt-1 text-xs">
                  {fieldErr('date')}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor={fid('startTime')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Hora de Início
              </label>
              <TimePicker
                id={fid('startTime')}
                value={p.startTime}
                onChange={(value) => setData('payload', { ...p, startTime: value })}
                required
              />
            </div>
            <div>
              <label
                htmlFor={fid('endTime')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Hora de Fim
              </label>
              <TimePicker
                id={fid('endTime')}
                value={p.endTime}
                onChange={(value) => setData('payload', { ...p, endTime: value })}
                hasError={!!fieldErr('endTime')}
                aria-invalid={!!fieldErr('endTime')}
                aria-describedby={fieldErr('endTime') ? errId('endTime') : undefined}
                required
              />
              {fieldErr('endTime') && (
                <p id={errId('endTime')} className="text-error mt-1 text-xs">
                  {fieldErr('endTime')}
                </p>
              )}
            </div>
          </div>
          <div className="md:w-1/3">
            <label
              htmlFor={fid('attendees')}
              className="text-on-surface mb-1.5 block text-sm font-medium"
            >
              Nº Estimado de Participantes
            </label>
            <input
              id={fid('attendees')}
              type="number"
              min="1"
              max="2000"
              className={inputClass}
              value={p.attendees}
              onChange={set('attendees')}
              placeholder="Ex: 50"
              required
            />
          </div>
        </div>

        {/* Observações */}
        <div>
          <label
            htmlFor={fid('notes')}
            className="text-on-surface mb-1.5 block text-sm font-medium"
          >
            Observações <span className="text-on-surface-light font-normal">(opcional)</span>
          </label>
          <textarea
            id={fid('notes')}
            className={`${inputClass} resize-none`}
            rows={4}
            value={p.notes}
            onChange={set('notes')}
            placeholder="Requisitos especiais, configuração do espaço, etc."
          />
        </div>

        {/* Consents */}
        <div className="space-y-3">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={data.consentTerms}
              onChange={(e) => setData('consentTerms', e.target.checked)}
              className="mt-1 rounded"
              required
            />
            <span className="text-on-surface-light text-sm leading-relaxed">
              Declaro tomar conhecimento de que a utilização dos espaços fica sujeita ao regulamento
              aplicável, disponível para consulta pela Junta de Freguesia, e comprometo-me a
              restituir o espaço nas condições em que foi cedido.
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={data.consentData}
              onChange={(e) => setData('consentData', e.target.checked)}
              className="mt-1 rounded"
              required
            />
            <span className="text-on-surface-light text-sm leading-relaxed">
              Tomei conhecimento do tratamento dos meus dados pessoais para efeitos deste pedido,
              nos termos da{' '}
              <Link href="/privacy" className="text-primary underline">
                política de privacidade
              </Link>
              .
            </span>
          </label>
        </div>

        <FormActions
          submitLabel="Submeter reserva"
          submittingLabel="A submeter…"
          submittingHint="A enviar os dados de forma segura"
          processing={processing}
          disabled={!isFormValid}
          onReset={() => {
            reset();
            setClientErrors({});
          }}
        />
      </form>
    </div>
  );
}
